import React, { useEffect, useMemo, useState } from 'react';
import { Input, Card, Row, Col, Spin, Empty, Space, Tag, Typography } from 'antd';
import { SearchOutlined, CheckCircleOutlined, ThunderboltOutlined, EditOutlined } from '@ant-design/icons';
import { flattenOllamaFamilies } from './localOllamaModelUtils';
import { resolveModelIconUrl } from '../../utils/modelIconUrl';
import './ModelFields.css';

const { Text, Paragraph } = Typography;

const DEBOUNCE_MS = 200;

/**
 * Search or type full Ollama tag; pick from catalog cards. Installed models first (API order).
 * Debounced substring filter smooths fast typing.
 * If nothing matches, offers a card to use the typed string; optional onPersistCustom saves it to the catalog.
 */
const LocalOllamaModelField = ({ value, onChange, families, loading, emptyDescription, onPersistCustom }) => {
    const flat = useMemo(() => flattenOllamaFamilies(families), [families]);
    const [debouncedValue, setDebouncedValue] = useState('');
    const [persisting, setPersisting] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedValue(value || ''), DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [value]);

    const filtered = useMemo(() => {
        const q = debouncedValue.trim().toLowerCase();
        if (!q) return flat;
        return flat.filter(
            (m) =>
                (m.fullName || '').toLowerCase().includes(q) ||
                (m.familyName || '').toLowerCase().includes(q),
        );
    }, [flat, debouncedValue]);

    const trimmed = (value || '').trim();
    const debouncedTrim = debouncedValue.trim();
    const exactInCatalog = trimmed ? flat.some((m) => m.fullName === trimmed) : false;
    const showUseTypedModel = Boolean(
        debouncedTrim.length > 0 &&
            !loading &&
            !exactInCatalog &&
            filtered.length === 0,
    );
    const manualSelected = Boolean(trimmed && !exactInCatalog && value != null && value.trim() === trimmed);

    const applyTypedModel = async () => {
        if (!trimmed || persisting) return;
        if (typeof onPersistCustom === 'function') {
            setPersisting(true);
            try {
                await onPersistCustom(trimmed);
            } catch (e) {
                console.error(e);
                return;
            } finally {
                setPersisting(false);
            }
        }
        onChange(trimmed);
    };

    return (
        <div className="local-ollama-model-field">
            <Input
                size="large"
                placeholder="Search catalog or type the full model name (e.g. llama3:8b)"
                prefix={<SearchOutlined style={{ color: 'var(--ant-color-text-quaternary, #bbb)' }} />}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                allowClear
                className="local-ollama-model-field-input"
            />
            <div className="local-ollama-model-field-list create-collection-model-scroll">
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 32 }}>
                        <Spin />
                    </div>
                ) : filtered.length > 0 ? (
                    <Row gutter={[8, 8]}>
                        {filtered.map((m) => {
                            const id = m.fullName;
                            const isSelected = value != null && value.trim() === id;
                            return (
                                <Col span={24} key={id}>
                                    <Card
                                        size="small"
                                        hoverable
                                        onClick={() => onChange(id)}
                                        className={`create-collection-model-card ${isSelected ? 'is-selected' : ''}`}
                                        bodyStyle={{ padding: '10px 12px' }}
                                    >
                                        <div className="create-collection-embedding-card-inner">
                                            <div className="create-collection-embedding-card-main">
                                                {m.familyIcon ? (
                                                    <img
                                                        src={resolveModelIconUrl(m.familyIcon)}
                                                        alt=""
                                                        className="create-collection-embedding-card-icon"
                                                    />
                                                ) : (
                                                    <div className="create-collection-embedding-card-icon-fallback">
                                                        <ThunderboltOutlined />
                                                    </div>
                                                )}
                                                <div className="create-collection-embedding-card-text">
                                                    <div className="create-collection-embedding-title-row">
                                                        <Text strong className="create-collection-embedding-card-name">
                                                            {m.fullName}
                                                        </Text>
                                                        {m.installed && (
                                                            <Tag color="success" className="local-ollama-installed-tag">
                                                                Installed
                                                            </Tag>
                                                        )}
                                                    </div>
                                                    {m.familyDescription && (
                                                        <Paragraph
                                                            type="secondary"
                                                            ellipsis={{ rows: 2 }}
                                                            className="create-collection-embedding-card-desc"
                                                        >
                                                            {m.familyDescription}
                                                        </Paragraph>
                                                    )}
                                                    <Space size={4} wrap className="create-collection-embedding-tags">
                                                        {m.size && <Tag>{m.size}</Tag>}
                                                        {m.context && <Tag>{m.context}</Tag>}
                                                        {m.input && <Tag>{m.input}</Tag>}
                                                    </Space>
                                                </div>
                                            </div>
                                            {isSelected && (
                                                <CheckCircleOutlined
                                                    className="create-collection-choice-card-check"
                                                    aria-hidden
                                                />
                                            )}
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                ) : showUseTypedModel ? (
                    <Row gutter={[8, 8]}>
                        <Col span={24}>
                            <Card
                                size="small"
                                hoverable
                                role="button"
                                tabIndex={0}
                                loading={persisting}
                                onClick={applyTypedModel}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        applyTypedModel();
                                    }
                                }}
                                className={`create-collection-model-card local-ollama-use-custom-card ${manualSelected ? 'is-selected' : ''}`}
                                bodyStyle={{ padding: '10px 12px' }}
                            >
                                <div className="create-collection-embedding-card-inner">
                                    <div className="create-collection-embedding-card-main">
                                        <div className="create-collection-embedding-card-icon-fallback local-ollama-use-custom-icon">
                                            <EditOutlined />
                                        </div>
                                        <div className="create-collection-embedding-card-text">
                                            <Text strong className="create-collection-embedding-card-name">
                                                Use this model name
                                            </Text>
                                            <Paragraph type="secondary" className="local-ollama-use-custom-code" copyable={{ text: trimmed }}>
                                                {trimmed}
                                            </Paragraph>
                                            <Text type="secondary" className="local-ollama-use-custom-hint">
                                                {onPersistCustom
                                                    ? 'Not in the catalog — click to save it and use this name with Ollama.'
                                                    : 'Not in the catalog list — will be sent to Ollama exactly as entered.'}
                                            </Text>
                                        </div>
                                    </div>
                                    {manualSelected && (
                                        <CheckCircleOutlined
                                            className="create-collection-choice-card-check"
                                            aria-hidden
                                        />
                                    )}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={emptyDescription || 'No models match. Check that Ollama is running and models are pulled.'}
                    />
                )}
            </div>
        </div>
    );
};

export default React.memo(LocalOllamaModelField);
